import { Inject, Injectable } from '@angular/core';
import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import { Subject } from 'rxjs';
import {
  signIn,
  updatePassword,
  signInWithRedirect,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  updateUserAttribute,
  confirmSignIn,
  confirmResetPassword,
  resetPassword,
  UpdateUserAttributeOutput,
} from 'aws-amplify/auth';
import { CognitoUser, EnvironmentInfo, SsoItem } from './model/environment.info.model';

export interface SsoSignInFailure {
  name?: string;
  message?: string;
  recoverySuggestion?: string;
}

export function extractMissingSamlAttributes(message?: string): string[] {
  if (!message) return [];
  const pattern = /([\w.]+):\s*The attribute(?:\s+\1)?\s+is required/gi;
  const attributes = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(message))) {
    attributes.add(match[1].split('.')[0]);
  }
  return [...attributes];
}

export function isSamlUserDisabledError(message?: string): boolean {
  if (!message) return false;
  return /user is disabled/i.test(message);
}
import { AWSAmplifyAuthInfo, AWSAmplifyAuthResult, AWSAmplifyLoginResponse } from './model/aws-amplify-auth-info';
import { APPNAME } from '../Constants/app-injection-token';
import { AUTH_SESSION_CHANNEL, AUTH_SESSION_MESSAGE_TYPE } from './auth-session-channel.constants';
import { getOrCreateTabId } from './auth-tab-id.util';
import { buildCognitoFullName } from './auth-cognito-full-name.util';

// const debug = !environment.isLocalhost && (environment.isDEV || environment.isQA || environment.isRC);
// Logger.LOG_LEVEL = debug ? 'DEBUG' : 'INFO';
// window['LOG_LEVEL'] = debug ? 'DEBUG' : 'INFO';

@Injectable({
  providedIn: 'root',
})
export class AuthCognitoService {
  private _environmentURL: string = location.origin;
  private readonly _tabId = getOrCreateTabId(typeof window === 'undefined' ? null : window);
  private readonly _ssoSignInFailure$ = new Subject<SsoSignInFailure | undefined>();
  private readonly _ssoSignInSuccess$ = new Subject<void>();
  public readonly ssoSignInFailure$ = this._ssoSignInFailure$.asObservable();
  public readonly ssoSignInSuccess$ = this._ssoSignInSuccess$.asObservable();

  set authenticatedUserToken(token: string) {
    localStorage.setItem('authenticatedUserToken', token);
  }
  get authenticatedUserToken() {
    return localStorage.getItem('authenticatedUserToken');
  }

  set authenticatedUserSub(sub: string | null) {
    if (sub === null) {
      localStorage.removeItem('authenticatedUserSub');
      localStorage.removeItem('authenticatedUserToken');
    } else {
      localStorage.setItem('authenticatedUserSub', sub);
    }
    this.notifyAuthUserChange(sub);
  }
  get authenticatedUserSub() {
    return localStorage.getItem('authenticatedUserSub');
  }

  public setEnvironmentURL(envURL: string) {
    this._environmentURL = envURL;
  }

  constructor(@Inject(APPNAME) public APPNAME: string) {
    Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect_failure') {
        this._ssoSignInFailure$.next(payload.data?.error);
      } else if (payload.event === 'signInWithRedirect') {
        this._ssoSignInSuccess$.next();
      }
    });
  }

  hasCognitoConfig(): boolean {
    return JSON.stringify(Amplify.getConfig() ?? {}) !== '{}';
  }

  applyCongnitoConfig(env: EnvironmentInfo): void {
    Amplify.configure({
      Auth: new AWSAmplifyAuthInfo(env, this.APPNAME),
    });
  }

  async ssoSignIn(sso: SsoItem, options?: { forceLogin?: boolean }): Promise<void> {
    await signInWithRedirect({
      provider: { custom: sso.id },
      options: options?.forceLogin ? { prompt: 'LOGIN' } : undefined,
    });
  }

  async isAlreadyAuthenticated(): Promise<boolean> {
    try {
      const user = await this.getCurrentUser();
      return !!user;
    } catch {
      return false;
    }
  }

  async signIn(credentials: { user: string; password: string }): Promise<AWSAmplifyLoginResponse> {
    if (await this.isAlreadyAuthenticated()) {
      return {
        result: AWSAmplifyAuthResult.Success,
        currentUser: await this.getCurrentUser(),
      };
    }
    const response = await signIn({
      username: credentials.user,
      password: credentials.password,
    });

    if (response.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      // AB#73695 - no valid session exists yet at this interim challenge state (the user
      // hasn't answered NEW_PASSWORD_REQUIRED), and no caller reads currentUser for this
      // branch. Fetching it anyway raced Amplify's own token cache against the immediately
      // following confirmSignIn() call, which does its own internal session fetch to finalize
      // sign-in - the two together intermittently failed with "Access Token has been revoked"
      // even though the password itself had already changed successfully.
      return {
        result: AWSAmplifyAuthResult.NewPasswordRequired,
        currentUser: null,
      };
    }

    await this.setAuthtenticatedUserInfo();

    return {
      result: AWSAmplifyAuthResult.Success,
      currentUser: await this.getCurrentUser(),
    };
  }

  private _pendingCurrentUser: Promise<CognitoUser | null> | null = null;

  getCurrentUser(): Promise<CognitoUser | null> {
    if (!this._pendingCurrentUser) {
      this._pendingCurrentUser = this.#fetchCurrentUser().finally(() => {
        this._pendingCurrentUser = null;
      });
    }
    return this._pendingCurrentUser;
  }

  async #fetchCurrentUser(): Promise<CognitoUser | null> {
    try {
      const [currentUser, session, attributes] = await Promise.all([getCurrentUser(), fetchAuthSession(), fetchUserAttributes()]);
      const isFederated = !!attributes && !!attributes.identities;
      const user = <CognitoUser>{
        ssoId: session.userSub,
        // AB#75346 - internal accounts have no SSO identity; backfilling this from the plain
        // username made every "is this account federated" check in the app wrongly say yes.
        ssoUsername: isFederated ? currentUser.username : undefined,
        username: attributes ? (attributes.identities && attributes.email ? attributes.email : currentUser.username.replace(/^sso_|(sso[1-4]_)/g, '')) : currentUser.username,
        accessToken: session.tokens.accessToken.toString(),
        firstName: attributes.given_name ?? attributes.name,
        lastName: attributes.family_name,
        // AB#69654 / AB#74839: some IdPs send the full display name in "name" (BMS/VExMO), others only the
        // first name(s), with the surname in "family_name" (Gerdau). See buildCognitoFullName.
        fullName: buildCognitoFullName(attributes),
        email: attributes.email,
        isFederated,
        baseUrl: this._environmentURL,
      };
      return user;
    } catch {
      return null;
    }
  }

  async sendVerificationCode(username: string): Promise<void> {
    const response = await resetPassword({ username });
    if (response.nextStep.resetPasswordStep !== 'CONFIRM_RESET_PASSWORD_WITH_CODE' && !response.isPasswordReset) {
      throw new Error();
    }
  }

  async resetPasswordWithVerificationCode(confirmationCode: string, username: string, newPassword: string): Promise<void> {
    await confirmResetPassword({ username, confirmationCode, newPassword });
  }

  async changeTemporaryPassword(password: string): Promise<void> {
    const response = await confirmSignIn({ challengeResponse: password });
    if (!response.isSignedIn) {
      throw new Error();
    }
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    return await updatePassword({ newPassword, oldPassword });
  }

  async signOut(): Promise<void> {
    try {
      await this.#globalSignOutSilently();
    } catch (e) {
      console.error(e);
    } finally {
      this.authenticatedUserSub = null;
    }
  }

  // AB#75346 - silent REST revoke, not Amplify's signOut(), which always redirects through the IdP.
  async #globalSignOutSilently(): Promise<void> {
    const session = await fetchAuthSession();
    const accessToken = session?.tokens?.accessToken?.toString();
    const userPoolId = Amplify.getConfig().Auth?.Cognito?.userPoolId;
    if (!accessToken || !userPoolId) return;

    const region = userPoolId.split('_')[0];
    await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.GlobalSignOut',
      },
      body: JSON.stringify({ AccessToken: accessToken }),
    });
  }

  // AB#75346 - IdP logout in a real (top-level) popup again, not a hidden iframe: SAML IdPs (e.g.
  // Microsoft Entra ID) send X-Frame-Options/CSP frame-ancestors on their login/logout pages, so an
  // iframe is silently blocked and the Single Logout redirect chain never runs - the disconnect
  // looked successful but never actually signed the user out of the IdP. A popup can't be made
  // invisible either: Chromium clamps off-screen coordinates back onto the opener's own screen, and
  // cross-origin content (Cognito's domain, then the IdP's) can't be styled or covered from our side
  // (Same-Origin Policy) - any attempt just flashes a blank/mismatched frame instead of hiding
  // anything. Left as a normal, modestly sized popup so the user can actually see the IdP's own
  // logout page progress instead of an unexplained flicker.
  disconnectIdpSilently(): Promise<void> {
    const cognitoConfig = Amplify.getConfig().Auth?.Cognito;
    const domain = cognitoConfig?.loginWith?.oauth?.domain;
    const clientId = cognitoConfig?.userPoolClientId;
    const redirectUri = cognitoConfig?.loginWith?.oauth?.redirectSignOut?.[0];
    if (!domain || !clientId || !redirectUri) return Promise.resolve();

    const logoutUrl = `https://${domain}/logout?client_id=${encodeURIComponent(clientId)}&logout_uri=${encodeURIComponent(redirectUri)}`;
    const popup = window.open(logoutUrl, 'solvace-idp-disconnect', 'width=500,height=650');
    if (!popup) return Promise.resolve();
    popup.opener = null;

    return new Promise((resolve) => this.#closePopupOnceDone(popup, redirectUri, resolve));
  }

  // AB#75346 - closes the popup itself once the IdP hands control back to our own origin, instead
  // of leaving it open indefinitely off-screen. A fallback setTimeout caps the wait, in case the IdP
  // never redirects back (e.g. its own logout page expects a click).
  #closePopupOnceDone(popup: Window, redirectUri: string, resolve: () => void): void {
    const maxWaitMs = 6000;
    const pollIntervalMs = 250;
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;
      clearInterval(poll);
      clearTimeout(maxWaitTimeout);
      if (!popup.closed) popup.close();
      resolve();
    };

    const maxWaitTimeout = globalThis.setTimeout(finish, maxWaitMs);

    const poll = globalThis.setInterval(() => {
      let backOnOwnOrigin = false;
      try {
        backOnOwnOrigin = popup.location.href.startsWith(redirectUri);
      } catch {
        backOnOwnOrigin = false;
      }

      if (popup.closed || backOnOwnOrigin) finish();
    }, pollIntervalMs);
  }

  async getUserAttribute(name: string) {
    try {
      const attributes = await fetchUserAttributes();
      return attributes?.[name];
    } catch {
      return null;
    }
  }

  async setUserAttribute(name: string, value: any): Promise<UpdateUserAttributeOutput> {
    try {
      return await updateUserAttribute({ userAttribute: { attributeKey: name, value } });
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  private async setAuthtenticatedUserInfo(): Promise<void> {
    const user = await getCurrentUser();
    const session = await fetchAuthSession();
    this.authenticatedUserSub = user?.userId;
    this.authenticatedUserToken = session?.tokens?.accessToken?.toString();
  }

  private notifyAuthUserChange(userSub: string | null): void {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;

    try {
      const channel = new BroadcastChannel(AUTH_SESSION_CHANNEL);
      channel.postMessage({ type: AUTH_SESSION_MESSAGE_TYPE, userSub, tabId: this._tabId });
      channel.close();
    } catch { }
  }
}
